import { Formik } from 'formik';

import { isMobile } from 'hooks';
import { ButtonColors, PaymentMethodType } from 'types';
import {
  Icon,
  Flex,
  Button,
  InfoBlock,
  DialogType,
  InputField,
  ButtonTypes,
  CommonStyles,
} from 'components';

import * as Styles from '../styles';
import { useRemainderCart } from '../hooks';
import { TemplateDialog } from '../template-dialog';
import { FullAmountButton } from '../full-amount-button';

import { CashProps, FormValues } from './types';
import { initialValues, generatePresets, validationSchema } from './config';

export const Cash = ({
  amount,
  onAddPaymentMethod,
  isProcessingFeeEnabled,
}: CashProps) => {
  const mobile = isMobile();
  const { remainder, isBalanceZero } = useRemainderCart(
    amount,
    isProcessingFeeEnabled
  );

  const presets = generatePresets(remainder);

  // Owner 18 Oct 2026: once a sale's balance is fully paid, Cash adds nothing
  // (it used to add a $0.00 cash line with the typed amount as "received").
  // The tile shows as covered, like the other locked tiles.
  const isSalePaid = amount > 0 && isBalanceZero;

  if (isSalePaid) {
    return (
      <InfoBlock disabled>
        <Styles.CardContainer>
          <Icon.Cash />
          <h3>Cash</h3>
        </Styles.CardContainer>
      </InfoBlock>
    );
  }

  return (
    <TemplateDialog title="Cash" icon={<Icon.Cash />}>
      {(close, _, ButtonWrap) => (
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (formValues) => {
            console.log('[Checkout] added cash in payment method', formValues);

            const enteredAmount = +formValues.amount;
            const cashAmount = Math.min(enteredAmount, remainder);

            // Never a $0.00 cash line on a sale with a total (e.g. the
            // balance was paid while this dialog was open).
            if (amount > 0 && !(cashAmount > 0)) {
              close();
              return;
            }

            onAddPaymentMethod({
              amount: cashAmount,
              totalAmount: cashAmount,
              id: `${cashAmount}-Cash`,
              type: PaymentMethodType.Cash,
              receivedCashAmount: enteredAmount,
            });
            close();
          }}
        >
          {({ dirty, values, isSubmitting, setFieldValue }) => (
            <CommonStyles.Form gap={24}>
              <Flex gap={16} flexDirection="column">
                <Flex flexDirection="column">
                  <InputField
                    number
                    decimal
                    name="amount"
                    label="Amount"
                    onBlur={() => {
                      const entered = +values.amount;
                      const cashAmount = Math.min(entered, remainder);
                      setFieldValue('amount', cashAmount);
                    }}
                  />

                  <FullAmountButton<FormValues>
                    remainder={remainder}
                    isBalanceZero={isBalanceZero}
                  />
                </Flex>

                <Flex gap={8} fullWidth>
                  {presets.map((preset, i) => (
                    <Button
                      key={i}
                      fullWidth
                      text={`$${preset.toFixed(2)}`}
                      colorType={ButtonColors.LightBlue}
                      onClick={() => {
                        setFieldValue('amount', preset);
                      }}
                    />
                  ))}
                </Flex>
              </Flex>

              <ButtonWrap type={DialogType.Plain}>
                <Button
                  text="Cancel"
                  onClick={close}
                  fullWidth={mobile}
                  colorType={ButtonColors.LightBlue}
                />
                <Button
                  text="Add"
                  fullWidth={mobile}
                  type={ButtonTypes.Submit}
                  disabled={!dirty || isSubmitting}
                />
              </ButtonWrap>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </TemplateDialog>
  );
};
